/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Mutation hooks for saved views operations
 */

import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { logger } from "@/lib/utils/logger";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SavedViewType } from "./types";
import { getSavedViewsEndpoint } from "@/lib/utils/saved-views/endpoints";
import { toast } from "sonner";

interface UseSavedViewsMutationsOptions {
  viewType: SavedViewType;
  organizationId: string | null;
  getToken: () => Promise<string | null>;
  queryKey: unknown[];
}

/**
 * Hook for saved views mutations (create, update, delete)
 */
export function useSavedViewsMutations({
  viewType,
  organizationId,
  getToken,
  queryKey,
}: UseSavedViewsMutationsOptions) {
  const queryClient = useQueryClient();
  const apiEndpoint = getSavedViewsEndpoint(viewType);

  const createMutation = useMutation({
    mutationFn: async (input: {
      name: string;
      filters: Record<string, unknown>;
      isOrgPreset?: boolean;
    }) => {
      if (!organizationId) throw new Error("Organization not found");

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId,
        autoRetry429: true,
      });

      if (viewType === "projects") {
        // Projects API
        const response = await api.post<{ view_id: string }>(apiEndpoint, {
          name: input.name,
          filters: input.filters,
          isOrgPreset: input.isOrgPreset ?? false,
        });

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to create saved view");
        }

        return response.data;
      } else if (viewType === "suppliers") {
        // Suppliers API - similar to projects
        const response = await api.post<{ view_id: string }>(apiEndpoint, {
          name: input.name,
          view_type: viewType,
          filters: input.filters,
          is_org_preset: input.isOrgPreset ?? false,
          is_default: false,
        });

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to create saved view");
        }

        return response.data;
      } else {
        // Clients/Contacts/Estimates API
        const response = await api.post<{ view_id: string }>(apiEndpoint, {
          name: input.name,
          view_type: viewType,
          filters: input.filters,
          is_org_preset: input.isOrgPreset ?? false,
          is_default: false,
        });

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to create saved view");
        }

        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Saved view created successfully");
    },
    onError: (error) => {
      logger.error("Failed to create saved view", { error, viewType });
      toast.error(
        error instanceof Error ? error.message : "Failed to create saved view",
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: {
      viewId: string;
      name?: string;
      filters?: Record<string, unknown>;
      isOrgPreset?: boolean;
    }) => {
      if (!organizationId) throw new Error("Organization not found");

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId,
        autoRetry429: true,
      });

      if (viewType === "projects") {
        // Projects API uses PATCH
        const response = await api.patch<{ view_id: string }>(
          `${apiEndpoint}/${input.viewId}`,
          {
            name: input.name,
            filters: input.filters,
            isOrgPreset: input.isOrgPreset,
          },
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to update saved view");
        }

        return response.data;
      } else if (viewType === "suppliers") {
        // Suppliers API uses PUT
        const response = await api.put<{ view_id: string }>(
          `${apiEndpoint}/${input.viewId}`,
          {
            name: input.name,
            filters: input.filters,
            is_org_preset: input.isOrgPreset,
          },
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to update saved view");
        }

        return response.data;
      } else {
        // Clients/Contacts/Estimates API uses PUT
        const response = await api.put<{ view_id: string }>(
          `${apiEndpoint}/${input.viewId}`,
          {
            name: input.name,
            filters: input.filters,
            is_org_preset: input.isOrgPreset,
          },
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to update saved view");
        }

        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Saved view updated successfully");
    },
    onError: (error) => {
      logger.error("Failed to update saved view", { error, viewType });
      toast.error(
        error instanceof Error ? error.message : "Failed to update saved view",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (viewId: string) => {
      if (!organizationId) throw new Error("Organization not found");

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId,
        autoRetry429: true,
      });

      const response = await api.delete(`${apiEndpoint}/${viewId}`);

      if (isApiFailure(response)) {
        throw new Error(response.message || "Failed to delete saved view");
      }

      return viewId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success("Saved view deleted successfully");
    },
    onError: (error) => {
      logger.error("Failed to delete saved view", { error, viewType });
      toast.error(
        error instanceof Error ? error.message : "Failed to delete saved view",
      );
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    createView: createMutation.mutateAsync,
    updateView: (
      viewId: string,
      input: {
        name?: string;
        filters?: Record<string, unknown>;
        isOrgPreset?: boolean;
      },
    ) => updateMutation.mutateAsync({ viewId, ...input }),
    deleteView: deleteMutation.mutateAsync,
  };
}
